import {
    Autocomplete,
    AutocompleteRenderInputParams,
    InputAdornment,
    ListItemIcon,
    ListItemText,
    Stack,
    TextField
} from "@mui/material";
import React, {useCallback, useMemo} from "react";
import {Office} from "../../api/Model";
import {TextFieldProps} from "@mui/material/TextField/TextField";
import {autocompleteChangeEvent, SetState} from "../../util/FormHandlers";
import {useOfficeStore} from "../../api/OfficeStore";
import {useRemoteValue} from "../../util/RemoteValue";
import {emptyOffice} from "../../api/Empty";
import {idFn} from "../../util/FunctionUtil";
import {OfficeIcon} from "../office-icon/OfficeIcon";


const optionLabel = (option: Office) => option.name;
const renderOption = ({key, ...props}: React.HTMLAttributes<HTMLLIElement> & { key: any }, office: Office) => (
    <Stack key={key} component="li" direction="row" spacing={2} justifyItems="stretch" {...props}>
        <ListItemIcon sx={{minWidth: 32}}>
            <OfficeIcon office={office} colored fontSize="medium"/>
        </ListItemIcon>
        <ListItemText
            primary={office.name}
            secondary={office.addressLineOne}
            slotProps={{primary: {sx: {textWrap: 'nowrap'}}}}
        />
    </Stack>
);

const renderOfficeInput = (
    params: AutocompleteRenderInputParams,
    props: TextFieldProps,
    office: Office | null,
) => (
    <TextField
        {...params}
        {...props}
        slotProps={{
            ...props.slotProps,
            input: {
                ...params.InputProps,
                ...props.slotProps?.input,
                startAdornment: office && office.id !== emptyOffice.id ? (
                    <>
                        <InputAdornment position="start">
                            <OfficeIcon office={office} fontSize="small"/>
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                    </>
                ) : params.InputProps.startAdornment,
            }
        }}
    />
);

export function OfficeSelect({office, setOffice, ...props}: {
    office: Office | null,
    setOffice: SetState<Office | null>,
} & TextFieldProps) {
    const officeStore = useOfficeStore();
    const offices = useRemoteValue(officeStore.remoteValue, []);

    const handleOfficeChange = useMemo(
        () => autocompleteChangeEvent((newOffice: Office | null) => setOffice(newOffice)),
        [setOffice],
    );

    const renderInput = useCallback(
        (params: AutocompleteRenderInputParams) => (
            renderOfficeInput(params, props, office)
        ), [office, props]);

    return (
        <Autocomplete<Office>
            options={offices}
            renderInput={renderInput}
            renderOption={renderOption}
            getOptionKey={idFn}
            getOptionLabel={optionLabel}
            value={office || null}
            disableClearable={false}
            onChange={handleOfficeChange}
            slotProps={{paper: {sx: {width: 'fit-content', minWidth: 280}}}}
        />
    );
}

export function OfficeSelectById({officeId, setOfficeId, ...props}: {
    officeId: number,
    setOfficeId: SetState<number>,
} & TextFieldProps) {
    const officeStore = useOfficeStore();
    const offices = useRemoteValue(officeStore.remoteValue, []);

    const office = useMemo(
        () => offices.find(office => office.id === officeId) || emptyOffice,
        [offices, officeId],
    );

    const handleOfficeChange = useMemo(
        () => autocompleteChangeEvent((newOffice: Office) => setOfficeId(newOffice.id)),
        [setOfficeId],
    );

    const renderInput = useCallback(
        (params: AutocompleteRenderInputParams) => (
            renderOfficeInput(params, props, office)
        ), [office, props]);

    return (
        <Autocomplete<Office, false, true>
            options={offices}
            renderInput={renderInput}
            renderOption={renderOption}
            getOptionKey={idFn}
            getOptionLabel={optionLabel}
            value={office}
            disableClearable={true}
            onChange={handleOfficeChange}

            slotProps={{paper: {sx: {width: 'fit-content', minWidth: 280}}}}
        />
    );
}
